// ─── "Set up secure chat" — first-run modal (CHAT_E2E_DESIGN.md §3, §8) ───────
// Shown once per user, the very first time they open chat on any device.
// Generates their X25519 identity keypair locally, publishes the public half,
// and asks them to set a Chat Recovery Passphrase — deliberately separate
// from their login password (an admin resetting the login password must
// never gain an indirect path to the private key, see design doc §3).
//
// Has a close (X) button that calls `onDismiss` — closes the chat drawer and
// returns the user to the rest of the CRM without completing setup. This
// used to be deliberately omitted (no thread can be opened on this device
// until its identity key exists, so dismissing doesn't "unblock" chat
// itself), but users reported being stuck looking at this modal with no way
// back to the CRM if they didn't have a passphrase ready right that moment.
// Dismissing just closes the drawer; chatKeyStatus is unchanged, so this
// same modal reappears next time chat is opened until setup is completed.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const MIN_PASSPHRASE_LENGTH = 8;

export default function SetupChatModal({ onComplete, onDismiss }) {
  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const tooShort = passphrase.length > 0 && passphrase.length < MIN_PASSPHRASE_LENGTH;
  const mismatch = confirm.length > 0 && passphrase !== confirm;
  const canSubmit = passphrase.length >= MIN_PASSPHRASE_LENGTH && passphrase === confirm && acknowledged && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await onComplete(passphrase);
    } catch (err) {
      console.error('[SetupChatModal]', err);
      setError(err.message || 'Something went wrong setting up secure chat. Please try again.');
      setSubmitting(false);
    }
    // On success the parent unmounts this modal (status flips to 'ready'),
    // so there's no success state to render here.
  };

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 480, position: 'relative' }}>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            title="Close and return to CRM"
            style={{
              position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: 8,
              background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, marginBottom: 14,
            background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Set up secure chat</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
            Messages here are end-to-end encrypted — not even an admin can read them.
            We'll generate your encryption keys now, right in this browser.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 8 }}>
              Chat Recovery Passphrase
            </label>
            <input
              type="password"
              className="crm-input"
              placeholder="At least 8 characters"
              value={passphrase}
              onChange={e => setPassphrase(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
            />
            {tooShort && (
              <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 6 }}>
                Use at least {MIN_PASSPHRASE_LENGTH} characters.
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, display: 'block', marginBottom: 8 }}>
              Confirm Passphrase
            </label>
            <input
              type="password"
              className="crm-input"
              placeholder="Re-enter your passphrase"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
            />
            {mismatch && (
              <div style={{ fontSize: 12, color: '#f87171', marginTop: 6 }}>
                Passphrases don't match.
              </div>
            )}
          </div>

          <div style={{
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: 10, padding: '10px 12px', marginBottom: 16, fontSize: 12.5,
            color: 'var(--text-secondary)', lineHeight: 1.5,
          }}>
            <strong style={{ color: '#f59e0b' }}>This is different from your login password</strong> and we
            never store it. If you forget it, no one — including admins — can recover your chat history.
            Write it down somewhere safe.
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 18, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              disabled={submitting}
              style={{ marginTop: 2 }}
            />
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              I understand that if I lose this passphrase, my chat history cannot be recovered by anyone.
            </span>
          </label>

          {error && (
            <div style={{ fontSize: 12.5, color: '#f87171', marginBottom: 14 }}>{error}</div>
          )}

          <button type="submit" className="btn-primary" disabled={!canSubmit} style={{ width: '100%', justifyContent: 'center' }}>
            {submitting ? 'Setting up…' : 'Create my chat keys'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
